/*
 * Utilities for building the Slot Preview payload from Chart Builder state
 */

import { generateGuid } from "./Utils";

export type BuilderStateLike = {
  // Optional Chart Builder state fields that may influence preview
  filters?: Record<string, string>;
  // Optional field affinities to inject under the first model's top-level `field_affinities`
  // Rationale: Do not conflate with parameters; this is separate model config for scoring
  fieldAffinities?: FieldAffinity[];
  // Optional conditional filters to inject at the model definition level
  conditionalFilters?: ConditionalFilters;
  userId?: string;
  seedId?: string;
  searchTerm?: string;
  responseSize?: number;
  zeroResultsOk?: boolean;
};

export type ChartPreviewPayload = {
  slotId: string;
  name: string;
  description?: string;
  type: string;
  experiments: any;
  zeroResultsOk?: boolean;
  responseSize?: number;
};

function deepClone<T>(obj: T): T {
  // Prefer native structuredClone when available; fallback to JSON clone for plain data
  const sc: any = (globalThis as any).structuredClone;
  if (typeof sc === "function") {
    try { return sc(obj); } catch { /* noop, fallback below */ }
  }
  return obj == null ? obj : JSON.parse(JSON.stringify(obj));
}

export type BuildChartPreviewPayloadOptions = {
  slotId: string;
  name?: string;
  type?: string;
  description?: string;
  // If you don't yet have a canonical experiments JSON, you can omit this and we will create a minimal stub
  experiments?: any;
  // Optionally pass a slice of the builder UI state so we can fold defaults (e.g., filters) into the experiments stub
  builderState?: BuilderStateLike;
  // Explicit overrides for response flags
  zeroResultsOk?: boolean;
  responseSize?: number;
};

/**
 * Build the object payload expected by POST /preview.
 *
 * Notes:
 * - If `experiments` is not provided, we create a minimal, forward-compatible stub and include current filter selections
 * Builds the object payload expected by POST /preview.
 *
 * If `experiments` is not provided, a minimal, forward-compatible stub is created and current filter selections
 * are included under an `experiments.filters` key. This should be replaced once the backend experiments schema is confirmed.
 *
 * @param {BuildChartPreviewPayloadOptions} options - Options for building the chart preview payload.
 * @param {string} options.slotId - The slot ID for the chart preview.
 * @param {string} [options.name] - The name of the chart preview.
 * @param {string} [options.type] - The type of the chart preview.
 * @param {string} [options.description] - Description of the chart preview.
 * @param {any} [options.experiments] - Experiments configuration. If omitted, a stub is generated.
 * @param {BuilderStateLike} [options.builderState] - Optional builder state to fold defaults (e.g., filters) into the experiments stub.
 * @param {boolean} [options.zeroResultsOk] - Explicit override for zero results flag.
 * @param {number} [options.responseSize] - Explicit override for response size.
 *
 * @returns {ChartPreviewPayload} The payload object to send to POST /preview.
 *
 * @example
 * // Basic usage with minimal options
 * const payload = buildChartPreviewPayloadObject({
 *   slotId: "slot_123",
 *   name: "Preview Chart",
 *   type: "dynamic",
 *   builderState: { filters: { category: "books" } }
 * });
 *
 * // Usage with custom experiments
 * const payload = buildChartPreviewPayloadObject({
 *   slotId: "slot_456",
 *   experiments: [{ id: "exp_1", percentage: 100, models: [{ id: "model_1", type: "static", parameters: {} }] }]
 * });
 */
export function buildChartPreviewPayloadObject(options: BuildChartPreviewPayloadOptions): ChartPreviewPayload {
  const {
    slotId,
    name = "Dynamic Chart Preview",
    type = "dynamic",
    description,
    experiments,
    builderState,
    zeroResultsOk,
    responseSize
  } = options;

  // UI-provided parameters (filters/factors mapped to params)
  const uiParams = builderState?.filters ?? {};

  // If no experiments provided, create a single hardcoded experiment with a single model definition
  // and inject parameters into the first model.
  const experimentsArray = Array.isArray(experiments) ? experiments : undefined;

  const defaultExperiment = [
    {
      id: generateGuid(),
      userPercentage: 100,
      isBaseRecipe: true,
      size: responseSize ?? builderState?.responseSize ?? 100,
      title: "",
      notes: "Default experiment for preview",
      disableModelPostFilter: true,
      modelDefinitions: [
        {
          key: "dynamic-chart-preview",
          version: 1,
          source: "DynamicChart",
          fulfilment: {
            ranges: [
              {
                start: 0,
                end: 50
              }
            ]
          },
          parameters: {
            isDynamicChart: true,
            defaultVariant: "7d",
            defaultOverlay: "play",
            chartName: "dynamic-chart-preview",
            // eslint-disable-next-line camelcase
            use_recommendation_id: true
          }
        }
      ]
    }
  ];

  const experimentsPayload: any[] = experimentsArray ?? defaultExperiment;

  // Ensure there is at least one experiment and one model with parameters
  if (!experimentsPayload[0]) {
    experimentsPayload[0] = defaultExperiment[0];
  }

  // Support both shapes: models[] and modelDefinitions[]
  const firstExperiment = experimentsPayload[0] as any;
  if (!firstExperiment.models && !firstExperiment.modelDefinitions) {
    firstExperiment.modelDefinitions = defaultExperiment[0].modelDefinitions;
  }

  // Ensure size is set on the first experiment if not already present
  if (firstExperiment.size === undefined) {
    firstExperiment.size = responseSize ?? builderState?.responseSize ?? 100;
  }

  const modelsArray = (firstExperiment.models ?? firstExperiment.modelDefinitions) as any[];
  if (!modelsArray[0]) {
    modelsArray[0] = defaultExperiment[0].modelDefinitions[0];
  }
  if (!modelsArray[0].parameters) {
    modelsArray[0].parameters = {};
  }

  // Deep clone parameters to avoid mutating any shared references
  const baseParams = modelsArray[0].parameters as Record<string, any>;
  const paramBag: Record<string, any> = deepClone(baseParams);
  // Reassign the cloned bag back to the model to ensure we only mutate the clone
  modelsArray[0].parameters = paramBag;

  // Inject conditional_filters (if provided) at the model definition level (not in parameters).
  // This allows the backend to evaluate AND/OR logic accurately during preview.
  if (builderState?.conditionalFilters && builderState.conditionalFilters.groups.length > 0) {
    // Omit empty groups/conditions before sending
    const cleanedGroups = builderState.conditionalFilters.groups
      .map(g => ({
        operator: g.operator,
        conditions: g.conditions.filter(c => c.topic && (c.values[0] || "") !== "")
      }))
      .filter(g => g.conditions.length > 0);

    if (cleanedGroups.length > 0) {
      // eslint-disable-next-line camelcase
      (modelsArray[0] as any).conditional_filters = {
        operator: builderState.conditionalFilters!.operator,
        groups: cleanedGroups
      };
    }
  }

  // Inject field affinities (if provided) as a top-level model property, not within parameters
  // Rationale: This matches backend contract and preserves backward compatibility of param handling
  if (builderState?.fieldAffinities && Array.isArray(builderState.fieldAffinities)) {
    // Transform field affinities to backend format (snake_case)
    const backendAffinities = builderState.fieldAffinities.map((aff: any) => {
      const backendAff: any = {
        field: aff.field,
        values: aff.values,
        score: aff.score,
        type: aff.type
      };
      // Convert newerIsProminent to newer_is_prominent for backend
      if (aff.newerIsProminent !== undefined) {
        // eslint-disable-next-line camelcase
        backendAff.newer_is_prominent = aff.newerIsProminent;
      }
      // Include days parameter for age type
      if (aff.days !== undefined) {
        backendAff.days = aff.days;
      }
      return backendAff;
    });
    // eslint-disable-next-line camelcase
    (modelsArray[0] as any).field_affinities = backendAffinities;
  }

  // Apply UI config on top of slot model parameters
  // - Start from the parameters already present on the model definition (slot)
  // - Overlay our UI-configured parameters
  // - If a UI parameter is unset/empty, remove it from the outgoing parameters
  // Do NOT inject userId/seedIds/q; those are passed via query string elsewhere

  Object.entries(uiParams).forEach(([k, v]) => {
    const isEmpty = v === undefined || v === null || `${v}` === "";
    if (isEmpty) {
      // Explicitly remove the key if user cleared it in the UI
      if (k in paramBag) delete paramBag[k];
    } else {
      paramBag[k] = v;
    }
  });

  return {
      slotId,
      name,
      description,
      type,
      experiments: experimentsPayload,
      zeroResultsOk: zeroResultsOk ?? builderState?.zeroResultsOk ?? undefined,
      responseSize: responseSize ?? builderState?.responseSize ?? undefined
  };
}

/**
 * Build the JSON string body expected by POST /preview.
 * Builds the JSON string body expected by POST /preview.
 *
 * This is a thin wrapper around {@link buildChartPreviewPayloadObject}, which constructs
 * the payload object. This function simply serializes that object to a JSON string.
 *
 * @param {BuildChartPreviewPayloadOptions} options - The options used to construct the chart preview payload.
 * @returns {string} The JSON string representation of the chart preview payload, suitable for POSTing to /preview.
 * @see buildChartPreviewPayloadObject
 */
export function buildChartPreviewPayload(options: BuildChartPreviewPayloadOptions): string {
  return JSON.stringify(buildChartPreviewPayloadObject(options));
}

